/* eslint-disable sonarjs/no-identical-functions */
import React, { useState, useEffect, useRef } from "react";
import styled, { css } from "styled-components";
import { NavLink } from "react-router-dom";
import PropTypes from "prop-types";

import {
    Colors,
    MediaQueries,
    Radii,
    Spacing,
    ZIndices
} from "@paclife/plds.tokens";
import { Icon } from "@paclife/plds.icon";

import { ReactComponent as Logo } from "assets/svg/pl-logo.svg";
import { FadeIn } from "styles/animations/Fade";
import { useOutsideClick } from "common/OutsideClickWrapper/OutsideClickWrapper";
import Typography from "common/Typography/Typography";
import LogoutModal from "components/LogoutModal/LogoutModal";

const MOBILE_HEADER_HEIGHT = "80px";

const Nav = styled.nav`
    width: 240px;
    height: 100vh;
    background-color: ${Colors.neutral100};
    padding: 140px 0 ${Spacing[32]};
    box-shadow: 0px 1px 3px rgba(38, 38, 38, 0.2); // no such token
    :hover {
        box-shadow: 0px 4px 15px rgba(38, 38, 38, 0.2); // no such token
    }

    display: flex;
    flex-direction: column;
    align-items: center;
    z-index: ${ZIndices.top};
    justify-content: space-between;

    position: relative;

    /** for tablet/mobile screen, 
    * expanded nav overlays on the main content */
    @media (max-width: ${MediaQueries.lg}) {
        position: absolute;
        top: 0;
        left: 0;
        z-index: ${ZIndices.focus};
    }

    transition: width 350ms;
    ${({ collapsed }) =>
        collapsed === "true" &&
        css`
            width: 56px;
            transition: width 350ms;
        `}

    ${({ minimized }) =>
        minimized === "true" &&
        css`
            display: none;
        `}
`;

const BrandLogo = styled(Logo)`
    position: absolute;
    top: 40px;
    left: 12px;
    ${({ collapsed }) =>
        collapsed === "true" &&
        css`
            display: none;
        `}
`;

const Section = styled.div`
    width: 100%;
    padding: 0 ${Spacing[16]};

    ${({ collapsed }) =>
        collapsed === "true" &&
        css`
            display: flex;
            flex-direction: column;
            align-items: center;
        `}
`;

const List = styled.div``;

const TabName = styled(Typography)`
    margin-left: ${Spacing[16]};
    color: ${Colors.neutral600};

    ${FadeIn}

    ${({ active }) =>
        active === "true" &&
        css`
            color: ${Colors.blue600};
        `};

    ${({ collapsed }) =>
        collapsed === "true" &&
        css`
            display: none;
        `}
`;

const StyledNavLink = styled(NavLink)`
    display: block;
    padding: 12px ${Spacing[16]};

    &.selected > p {
        color: ${Colors.blue600};
    }
    &.selected > svg {
        fill: ${Colors.blue600};
    }
`;

const ListItemStyles = css`
    cursor: pointer;
    border-radius: ${Radii.rounded};
    &:not(:last-child) {
        margin-bottom: 12px;
    }

    :hover {
        background-color: ${Colors.neutral200};
    }

    ${({ secondary }) =>
        secondary &&
        css`
            :hover {
                background-color: ${Colors.neutral100};
                ${TabName} {
                    color: ${Colors.blue600};
                }

                .secondary-item-icon {
                    fill: ${Colors.blue500};
                }
            }
        `}
`;

const ListItem = styled.li`
    ${ListItemStyles}
    & > a, div {
        padding: 12px ${Spacing[16]};
    }
    ${({ collapsed }) =>
        collapsed === "true" &&
        css`
            & > a,
            div {
                padding: ${Spacing[8]};
            }
        `}
`;

const AvatarDiv = styled.div`
    display: flex;
    align-items: center;
    padding: 0 ${Spacing[16]};
`;

const Avatar = styled.div`
    cursor: pointer;
    border-radius: ${Radii.circle};
    width: 40px;
    height: 40px;
    min-width: 40px;
    min-height: 40px;
    color: ${Colors.neutral0};
    background-color: ${Colors.blue700};
    display: flex;
    justify-content: center;
    align-items: center;

    box-shadow: 0px 1px 3px rgba(38, 38, 38, 0.2); // no such token
    :hover {
        box-shadow: 0px 4px 15px rgba(38, 38, 38, 0.2); // no such token
    }

    ${({ collapsed }) =>
        collapsed === "true" &&
        css`
            width: 32px;
        `}
`;

const Info = styled.div`
    margin-left: ${Spacing[16]};
    ${({ collapsed }) =>
        collapsed === "true" &&
        css`
            display: none;
        `}
`;

const Subtitle = styled(Typography)`
    display: block;
    max-width: 120px;
    color: ${Colors.neutral600};
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`;

const DividerLine = styled.hr`
    height: 1px;
    width: 176px;
    background-color: ${Colors.neutral300};
    margin: ${Spacing[24]} ${Spacing[16]};
    border: none;
    transition: width 350ms;
    ${({ collapsed }) =>
        collapsed === "true" &&
        css`
            width: 32px;
            transition: width 350ms;
        `}
`;

const ArrowIconWrapper = styled.span`
    position: absolute;
    top: 16px;
    right: 16px;
    cursor: pointer;
    outline: none;
`;

/** Need confirm with designer for MOBILE STYLE */
const MobileHeader = styled.div`
    background-color: ${Colors.neutral0};
    height: ${MOBILE_HEADER_HEIGHT};
    position: sticky;
    top: 0;
    left: 0;
    z-index: ${ZIndices.focus};
    display: flex;
    align-items: center;
    justify-content: center;

    @media (min-width: ${MediaQueries.md}) {
        display: none;
    }
`;

const MenuIcon = styled.span`
    position: absolute;
    top: 28px;
    left: 16px;
    cursor: pointer;
    outline: none;
`;

const ShadowDiv = styled.div`
    width: 56px;
`;

const Navigation = ({
    firstName,
    lastName,
    subtitle,
    menu,
    collapsed,
    setCollapsed
}) => {
    const [minimized, setMinimized] = useState(true);
    const [delayedOpen, setDelayedOpen] = useState(false);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [key, setKey] = useState("home");

    /** Domain keyword */
    const RIA = "ria";
    const AnnuitiesFP = "annuities";
    const PacificFunds = "pacificfunds";
    const AnnuitiesMyAccount = "myaccount";

    useEffect(() => {
        const hostname = document.location.hostname;
        if (hostname.includes(AnnuitiesMyAccount)) {
            setKey("home");
        } else if (hostname.includes(RIA) || hostname.includes(AnnuitiesFP)) {
            setKey("dashboard");
        } else if (hostname.includes(PacificFunds)) {
            setKey("acctMgmt");
        }
    }, []);

    /** Day 1 solution for Top Nav */
    const LEFT_TEXT_LINK = {
        dashboard: { text: "My Dashboard", link: "/dashboard" },
        acctMgmt: {
            text: "My Accounts",
            link: "/acctmgmt-ui/"
        },
        home: { text: "Back to Home", link: "/" }
    };

    useEffect(() => {
        let timerId;
        if (collapsed) {
            setDelayedOpen(false);
        } else {
            timerId = setTimeout(() => setDelayedOpen(true), 200);
        }
        return () => clearTimeout(timerId);
    }, [collapsed]);

    /** Automatically collapse the nav when it is a md screen size
     */
    useEffect(() => {
        const collapseNav = () => {
            const isMobileScreen =
                window.innerWidth <= parseInt(MediaQueries.md, 10);
            const isTabScreen =
                window.innerWidth > parseInt(MediaQueries.md, 10) &&
                window.innerWidth < parseInt(MediaQueries.lg, 10);
            if (isMobileScreen) {
                setMinimized(true);
                setCollapsed(false);
            } else if (isTabScreen) {
                setCollapsed(true);
                setMinimized(false);
            } else {
                setCollapsed(false);
                setMinimized(false);
            }
        };
        collapseNav();
        window.addEventListener("resize", collapseNav);
        // eslint-disable-next-line no-unused-expressions
    }, []);

    const showNavBar = () => {
        setMinimized(false);
        setCollapsed(false);
    };

    /** For md/sm screen, when a nav link is clicked, collapse/hide the nav */
    const handleClick = () => {
        if (window.innerWidth < parseInt(MediaQueries.md, 10)) {
            setMinimized(true);
        } else if (window.innerWidth < parseInt(MediaQueries.lg, 10)) {
            setCollapsed(true);
        }
    };

    /** handle outside click for md/sm screen */
    const wrapperRef = useRef();
    useOutsideClick(wrapperRef, () => {
        handleClick();
    });

    const toggleIcon = () => {
        if (window.innerWidth < parseInt(MediaQueries.md, 10)) {
            setMinimized(!minimized);
        } else {
            setCollapsed(!collapsed);
        }
    };

    return (
        <div ref={wrapperRef}>
            <MobileHeader>
                <MenuIcon onClick={showNavBar}>
                    <Icon name="menu" width={24} color={Colors.blue500} />
                </MenuIcon>
                <Logo width="168" height="56" />
            </MobileHeader>
            <Nav collapsed={String(collapsed)} minimized={String(minimized)}>
                <ArrowIconWrapper
                    role="button"
                    tabIndex={0}
                    onClick={toggleIcon}
                    onKeyDown={toggleIcon}
                >
                    <Icon
                        name={
                            collapsed ? "doubleRightArrow" : "doubleLeftArrow"
                        }
                        width={24}
                        color={Colors.blue500}
                    />
                </ArrowIconWrapper>
                <BrandLogo
                    width="200"
                    height="66.67"
                    collapsed={String(collapsed)}
                    onClick={() =>
                        (document.location.href = document.location.origin)
                    }
                />
                <Section collapsed={String(collapsed)}>
                    <ul>
                        {menu.map((nav) => {
                            return (
                                <ListItem
                                    key={nav.path}
                                    collapsed={String(collapsed)}
                                    onClick={handleClick}
                                >
                                    <StyledNavLink
                                        to={nav.path}
                                        activeClassName="selected"
                                    >
                                        <Icon
                                            name={nav.icon}
                                            width="24"
                                            color={Colors.neutral500}
                                        />
                                        {delayedOpen && (
                                            <TabName variant="body" bold>
                                                {nav.name}
                                            </TabName>
                                        )}
                                    </StyledNavLink>
                                </ListItem>
                            );
                        })}
                    </ul>
                </Section>
                <Section collapsed={String(collapsed)}>
                    <AvatarDiv>
                        <Avatar>
                            {(firstName[0] + lastName[0]).toUpperCase()}
                        </Avatar>
                        {delayedOpen && (
                            <Info>
                                <Typography variant="body" bold display="block">
                                    {firstName} {lastName}
                                </Typography>
                                <Subtitle variant="microcopy">
                                    {subtitle}
                                </Subtitle>
                            </Info>
                        )}
                    </AvatarDiv>
                    <DividerLine collapsed={String(collapsed)} />
                    <List>
                        <ListItem collapsed={String(collapsed)} secondary>
                            <a href={LEFT_TEXT_LINK[key].link}>
                                <Icon
                                    className="secondary-item-icon"
                                    name="computer"
                                    width={24}
                                    color={Colors.neutral500}
                                />
                                {delayedOpen && (
                                    <TabName variant="body" bold>
                                        {LEFT_TEXT_LINK[key].text}
                                    </TabName>
                                )}
                            </a>
                        </ListItem>
                        <ListItem
                            onClick={() => setShowLogoutModal(true)}
                            collapsed={String(collapsed)}
                            secondary
                        >
                            <div>
                                <Icon
                                    className="secondary-item-icon"
                                    name="logout"
                                    width={24}
                                    color={Colors.neutral500}
                                />
                                {delayedOpen && (
                                    <TabName variant="body" bold>
                                        Logout
                                    </TabName>
                                )}
                            </div>
                        </ListItem>
                    </List>
                </Section>
            </Nav>
            <ShadowDiv />
            {showLogoutModal && (
                <LogoutModal closeModal={() => setShowLogoutModal(false)} />
            )}
        </div>
    );
};

export default Navigation;

Navigation.propTypes = {
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    subtitle: PropTypes.string.isRequired,
    menu: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            icon: PropTypes.string.isRequired,
            path: PropTypes.string.isRequired
        })
    ).isRequired,
    collapsed: PropTypes.bool.isRequired,
    setCollapsed: PropTypes.func.isRequired
};
